// @ts-check
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Comment from './Comment';
import styles from './ReferralEvent.module.scss';
import pluralize from 'pluralize';
import Tooltip from './Tooltip';
import { FaxLink } from './FaxLink';

// TODO: Make this a union type between the different possible Event types
/** @typedef {{
  id: string,
  type: string,
  userOrganizationName?: string,
  userName: string,
  createdAt: string,
  data: any // todo,
  isPrivate?: boolean
}} Event */

/** @typedef {{
  event: Event,
  headers: React.ReactNode,
  content?: React.ReactNode
}} EventTemplatePropsType */

/** @type { React.FC<EventTemplatePropsType> } */
const EventTemplate = ({ event, headers, content }) => (
  <div
    className={styles.event}
    data-spec-id={`event-${event.id}`}
    data-spec-class={event.type}
  >
    <div>
      {event.userOrganizationName ? (
        <Tooltip
          id={event.id}
          renderTooltip={() => <span>{event.userOrganizationName}</span>}
        >
          <span className={styles.user}>{event.userName}</span>
        </Tooltip>
      ) : (
        <span className={styles.user}>{event.userName}</span>
      )}{' '}
      {headers} <span className={styles.timestamp}> {event.createdAt}</span>
    </div>
    {content ? <div>{content}</div> : null}
  </div>
);

EventTemplate.propTypes = {
  event: PropTypes.object.isRequired,
  headers: PropTypes.object.isRequired,
  content: PropTypes.object,
};

const ReferralReasonForDelayEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>update the referral's reason for delay: {event.data.reason}</span>
    }
  />
);

const ReferralReportCreatedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>created a referral report</span>}
  />
);

const ReferralReportUpdatedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>updated the referral report memo</span>}
  />
);

const ReferralReportAcknowledgedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>acknowledged the referral report</span>}
  />
);

const ReferralChecklistEvent = ({ event }) => {
  switch (event.data.itemAction) {
    case 'add':
      return (
        <EventTemplate
          event={event}
          headers={
            <span>
              added a new checklist item: {event.data.itemDescription}
            </span>
          }
        />
      );
    case 'remove':
      return (
        <EventTemplate
          event={event}
          headers={
            <span>removed a checklist item: {event.data.itemDescription}</span>
          }
        />
      );
    case 'check':
      return (
        <EventTemplate
          event={event}
          headers={
            <span>
              completed a checklist item: {event.data.itemDescription}
            </span>
          }
        />
      );
    case 'uncheck':
      return (
        <EventTemplate
          event={event}
          headers={
            <span>
              marked a previously completed checklist item as incomplete:{' '}
              {event.data.itemDescription}
            </span>
          }
        />
      );
    default:
      return null;
  }
};

const ReferralReminderEvent = ({ event }) => {
  switch (event.data.action) {
    case 'created':
      return (
        <EventTemplate
          event={event}
          headers={<span>created a reminder: {event.data.note}</span>}
        />
      );
    case 'incompleted':
      return (
        <EventTemplate
          event={event}
          headers={
            <span>
              marked a previously completed reminder as incomplete:{' '}
              {event.data.note}
            </span>
          }
        />
      );
    case 'completed':
      return (
        <EventTemplate
          event={event}
          headers={<span>completed a reminder: {event.data.note}</span>}
        />
      );
    case 'deleted':
      return (
        <EventTemplate
          event={event}
          headers={<span>deleted a reminder: {event.data.note}</span>}
        />
      );
    default:
      return null;
  }
};

const ReferralAppointmentConfirmedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>confirmed the appointment</span>}
  />
);

const ReferralAppointmentCompletedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>marked the appointment as completed</span>}
  />
);

const ReferralAppointmentUncompletedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>marked the appointment as not completed</span>}
  />
);

const ReferralAppointmentNoShowedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>no showed the appointment:</span>}
    content={<div className={styles.bubble}>{event.data.reason}</div>}
  />
);

const ReferralAppointmentCancelledEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>cancelled the appointment:</span>}
    content={<div className={styles.bubble}>{event.data.reason}</div>}
  />
);

const ReferralRescheduledEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>rescheduled this referral for {event.data.appointmentTime}</span>
    }
  />
);

const ReferralScheduledEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>scheduled this referral for {event.data.appointmentTime}</span>
    }
  />
);

const ReferralTransferredEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>transferred this referral to {event.data.transferredToName}</span>
    }
  />
);

const ReferralAssignedProviderEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>selected a provider: {event.data.providerName}</span>}
  />
);

const ReferralUnassignedProviderEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>
        removed the assigned the provider ({event.data.unassignedProviderName})
      </span>
    }
  />
);

const ReferralAssignedLocationEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>selected a location: {event.data.locationName}</span>}
  />
);

const ReferralUnassignedLocationEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>
        removed the assigned location ({event.data.unassignedLocationName})
      </span>
    }
  />
);

const ReferralAssignedAgentEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>
        assigned {event.data.agentFirstName} {event.data.agentLastName} to
        manage this referral
      </span>
    }
  />
);

const ReferralUnassignedAgentEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>
        unassigned {event.data.unassignedAgentFirstName}{' '}
        {event.data.unassignedAgentLastName}
      </span>
    }
  />
);

const ReferralReplacedEvent = ({ event }) => (
  <EventTemplate event={event} headers={<span>replaced this referral.</span>} />
);

const ReferralRejectionEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>rejected this referral:</span>}
    content={<div className={styles.bubble}>{event.data.reason}</div>}
  />
);

const ReferralExhaustedEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>marked this referral as exhausted</span>}
  />
);

const ReferralWithdrawalEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>withdrew this referral:</span>}
    content={<div className={styles.bubble}>{event.data.reason}</div>}
  />
);

const ReferralRemovedFileEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>removed a file:</span>}
    content={
      <ul>
        <li>{event.data.name}</li>
      </ul>
    }
  />
);

const ReferralReportRemovedFileEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={<span>removed a file from the report:</span>}
    content={
      <ul>
        <li>{event.data.name}</li>
      </ul>
    }
  />
);

/** @type { React.FC<{event: Event}> } */
const ReferralCommentEvent = ({ event }) => (
  <div
    className={styles.comment_container}
    data-spec-id={`event-${event.id}`}
    data-spec-class={event.type}
  >
    <Comment
      id={event.id}
      userName={event.userName}
      userOrganizationName={event.userOrganizationName}
      createdAt={event.createdAt}
      comment={event.data}
      isPrivate={event.isPrivate}
    />
  </div>
);

const ReferralUploadedFilesEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>uploaded {pluralize('file', event.data.files.length, true)}:</span>
    }
    content={
      <ul className="py-1 pl-10 text-xs list-disc">
        {event.data.files.map((file) => (
          <li key={file.id}>{file.name}</li>
        ))}
      </ul>
    }
  />
);

const ReferralReportUploadedFilesEvent = ({ event }) => (
  <EventTemplate
    event={event}
    headers={
      <span>
        added {pluralize('file', event.data.files.length, true)} to the report:
      </span>
    }
    content={
      <ul>
        {event.data.files.map((file) => (
          <li key={file.id}>{file.name}</li>
        ))}
      </ul>
    }
  />
);

const ReferralFaxQueuedEvent = ({ event }) => {
  return (
    <EventTemplate
      event={event}
      headers={
        <>
          <span>
            queued a {event.data.faxKind} fax to {event.data.faxNumber}
          </span>
          <span>
            {event.data.documentPresent ? (
              <FaxLink faxId={event.data.faxId} />
            ) : null}
          </span>
        </>
      }
    />
  );
};

const ReferralFaxFailedEvent = ({ event }) => {
  return (
    <EventTemplate
      event={event}
      headers={
        <>
          <span>
            was unable to send the {event.data.faxKind} fax to{' '}
            {event.data.faxNumber}
          </span>
          <span>
            {event.data.documentPresent ? (
              <FaxLink faxId={event.data.faxId} />
            ) : null}
          </span>
        </>
      }
    />
  );
};

const ReferralFaxSentEvent = ({ event }) => {
  return (
    <EventTemplate
      event={event}
      headers={
        <>
          <span>
            sent the {event.data.faxKind} fax to {event.data.faxNumber}
          </span>
          <span>
            {event.data.documentPresent ? (
              <FaxLink faxId={event.data.faxId} />
            ) : null}
          </span>
        </>
      }
    />
  );
};

/** @typedef {{
  event: Event
}} ReferralEventPropsType */

/** @augments { Component<ReferralEventPropsType> } */
class ReferralEvent extends Component {
  render() {
    const { event } = this.props;
    switch (event.type) {
      case 'ReferralCommentEvent':
        return <ReferralCommentEvent event={event} />;
      case 'ReferralUploadedFilesEvent':
        return <ReferralUploadedFilesEvent event={event} />;
      case 'ReferralRemovedFileEvent':
        return <ReferralRemovedFileEvent event={event} />;
      case 'ReferralWithdrawalEvent':
        return <ReferralWithdrawalEvent event={event} />;
      case 'ReferralRejectionEvent':
        return <ReferralRejectionEvent event={event} />;
      case 'ReferralExhaustedEvent':
        return <ReferralExhaustedEvent event={event} />;
      case 'ReferralReplacedEvent':
        return <ReferralReplacedEvent event={event} />;
      case 'ReferralAssignedLocationEvent':
        return <ReferralAssignedLocationEvent event={event} />;
      case 'ReferralUnassignedLocationEvent':
        return <ReferralUnassignedLocationEvent event={event} />;
      case 'ReferralAssignedAgentEvent':
        return <ReferralAssignedAgentEvent event={event} />;
      case 'ReferralUnassignedAgentEvent':
        return <ReferralUnassignedAgentEvent event={event} />;
      case 'ReferralAssignedProviderEvent':
        return <ReferralAssignedProviderEvent event={event} />;
      case 'ReferralUnassignedProviderEvent':
        return <ReferralUnassignedProviderEvent event={event} />;
      case 'ReferralTransferredEvent':
        return <ReferralTransferredEvent event={event} />;
      case 'ReferralScheduledEvent':
        return <ReferralScheduledEvent event={event} />;
      case 'ReferralRescheduledEvent':
        return <ReferralRescheduledEvent event={event} />;
      case 'ReferralAppointmentCancelledEvent':
        return <ReferralAppointmentCancelledEvent event={event} />;
      case 'ReferralAppointmentNoShowedEvent':
        return <ReferralAppointmentNoShowedEvent event={event} />;
      case 'ReferralAppointmentConfirmedEvent':
        return <ReferralAppointmentConfirmedEvent event={event} />;
      case 'ReferralAppointmentCompletedEvent':
        return <ReferralAppointmentCompletedEvent event={event} />;
      case 'ReferralAppointmentUncompletedEvent':
        return <ReferralAppointmentUncompletedEvent event={event} />;
      case 'ReferralChecklistEvent':
        return <ReferralChecklistEvent event={event} />;
      case 'ReferralReminderEvent':
        return <ReferralReminderEvent event={event} />;
      case 'ReferralReportCreatedEvent':
        return <ReferralReportCreatedEvent event={event} />;
      case 'ReferralReportUpdatedEvent':
        return <ReferralReportUpdatedEvent event={event} />;
      case 'ReferralReportAcknowledgedEvent':
        return <ReferralReportAcknowledgedEvent event={event} />;
      case 'ReferralReportUploadedFilesEvent':
        return <ReferralReportUploadedFilesEvent event={event} />;
      case 'ReferralReportRemovedFileEvent':
        return <ReferralReportRemovedFileEvent event={event} />;
      case 'ReferralFaxQueuedEvent':
        return <ReferralFaxQueuedEvent event={event} />;
      case 'ReferralFaxFailedEvent':
        return <ReferralFaxFailedEvent event={event} />;
      case 'ReferralFaxSentEvent':
        return <ReferralFaxSentEvent event={event} />;
      case 'ReferralReasonForDelayEvent':
        return <ReferralReasonForDelayEvent event={event} />;
      default:
        return null;
    }
  }
}

ReferralEvent.propTypes = {
  event: PropTypes.object.isRequired,
};

export default ReferralEvent;
